import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import { IconBell, IconCheck, IconChevronDown } from '@tabler/icons-react'
import { useMe } from '../api/queries'
import { useExperience } from '../experiences/useExperience'
import { AppearanceControl } from '../theme/AppearanceControl'
import { IconButton, Popover } from '../ui/kit'
import { api } from '../api/http'
import { localManagementUrl } from './localManagement'

const NAV = [
  { to: '/', label: 'Properties' },
  { to: '/lists', label: 'Lists' },
  { to: '/deal-models', label: 'Deal Models' },
  { to: '/settings', label: 'Settings' },
] as const

/** "Experience: Investor ›" — one control that scales to HMO, SA and whatever comes next. */
function ExperiencePicker() {
  const experience = useExperience()
  const [open, setOpen] = useState(false)
  if (experience.available.length <= 1 && experience.ready) return <span className="hdr-exp" title="Your experience"><span className="dot" /><b>{experience.spec.label}</b></span>
  return (
    <Popover open={open} onClose={() => setOpen(false)} align="end" width={260} anchor={
      <button type="button" className="hdr-exp" onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open} disabled={experience.switching} title="Switch experience">
        <span>Experience</span><b>{experience.spec.label}</b><IconChevronDown className="ic" aria-hidden="true" />
      </button>
    }>
      <div className="pop-title">Experience</div>
      <div className="pop-list" role="listbox" aria-label="Experience">
        {experience.available.map(spec => (
          <button type="button" role="option" aria-selected={spec.id === experience.active} key={spec.id} className={spec.id === experience.active ? 'is-on' : ''} disabled={experience.switching} onClick={() => { experience.switchTo(spec.id); setOpen(false) }}>
            <span className="opt-main"><span className="opt-label">{spec.label}</span><small>{spec.models.kind === 'fixed' ? 'One shared model, personal results' : 'Your chosen models rate the cards'}</small></span>
            {spec.id === experience.active ? <IconCheck className="ic-sm" aria-hidden="true" /> : null}
          </button>
        ))}
      </div>
      <div className="pop-note">The experience decides which figures, filters and settings you see. More experiences appear here as they are released.</div>
      {experience.error ? <div className="fld-error">{experience.error}</div> : null}
    </Popover>
  )
}

export function Header() {
  const pathname = useRouterState({ select: s => s.location.pathname })
  const me = useMe()
  const [menu, setMenu] = useState(false)
  const managementUrl = me.data?.role === 'owner' ? localManagementUrl() : null
  const logout = useMutation({
    mutationFn: () => api('logout'),
    onSuccess: () => window.location.assign('/login'),
  })
  return (
    <header className="hdr">
      <Link to="/" className="brand" aria-label="PropAlpha home">
        <span className="mark"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2L2 19.5h5.5L12 10.5l4.5 9H22L12 2z" /></svg></span>
        <span className="word">Prop<em>Alpha</em></span>
        <span className="tag">NEXT</span>
      </Link>
      <nav className="nav" aria-label="Primary">
        {NAV.map(item => {
          const on = item.to === '/' ? pathname === '/' || pathname.startsWith('/properties') : pathname.startsWith(item.to)
          return <Link key={item.to} to={item.to} className={on ? 'is-on' : ''}>{item.label}</Link>
        })}
      </nav>
      <span className="sp" />
      <ExperiencePicker />
      <div className="hdr-acts">
        <IconButton label="Notifications" badge={false}><IconBell className="ic" aria-hidden="true" /></IconButton>
        <AppearanceControl />
        <Popover open={menu} onClose={() => setMenu(false)} align="end" anchor={
          <button type="button" className="avatar" onClick={() => setMenu(m => !m)} aria-haspopup="dialog" aria-expanded={menu} aria-label="Account">
            {(me.data?.displayName ?? '··').slice(0, 2).toUpperCase()}
          </button>
        }>
          <div className="pop-title">{me.data?.displayName ?? 'Account'}</div>
          <div className="t-muted">{me.data?.email}</div>
          <div className="hdr-status"><span className="dot" />{me.data?.role}{me.data?.trustedInternal ? ' · trusted internal' : ''}</div>
          {managementUrl ? <div className="pop-label">Management</div> : null}
          <div className="pop-list">
            {managementUrl ? <a className="btn is-small" href={managementUrl} target="_blank" rel="noreferrer">Operations</a> : <button className="btn is-small" type="button" disabled={logout.isPending} onClick={() => logout.mutate()}>Sign out</button>}
          </div>
          {logout.isError ? <div className="fld-error">{logout.error.message}</div> : null}
        </Popover>
      </div>
    </header>
  )
}
